/* eslint-disable camelcase */
import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import { debounce, snakeCase } from 'lodash-es';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { ButtonGroup, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { updateUser } from '../../../actions/auth.actions';
import AsyncSelect from '../../../shared/components/AsyncSelect';
import Button from '../../../shared/components/Button';
import FormControl from '../../../shared/components/Form/FormControl';
import ResponsiveHeader from '../../../shared/components/ResponsiveHeader';
import Select from '../../../shared/components/Select';
import useBreakpoint from '../../../shared/hooks/useBreakpoint';
import useIsMounted from '../../../shared/hooks/useIsMounted';
import { GENDER_TYPES } from '../../../utils/constants';
import i18n from '../../../utils/i18n';
import { useRef } from 'react';

const validationSchema = Yup.object().shape({
  city_id: Yup.number()
    .required(`${i18n.t('influencers.common.city')} ${i18n.t('influencers.common.is_required')}`)
    .nullable(),
  dob: Yup.date()
    .required(`${i18n.t('influencers.common.birthday')} ${i18n.t('influencers.common.is_required')}`),
  gender: Yup.string()
    .required(`${i18n.t('influencers.common.gender')} ${i18n.t('influencers.common.is_required')}`),
});

const SignUpStep3 = ({ user }) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector(store => get(store, 'auth.user'));
  const currentUser = user || sessionUser;
  const userId = get(currentUser, 'id');
  const isMounted = useIsMounted();
  const breakpoint = useBreakpoint();
  const history = useHistory();

  const [state, setState] = useState({
    loading: false,
    loadingCities: false,
  });

  const formik = useFormik({
    initialValues: {
      city_id: get(currentUser, 'city_id') || null,
      dob: get(currentUser, 'dob') || '',
      gender: get(currentUser, 'gender') || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: values => {
      setState({ ...state, loading: true });
      const user = {
        ...values,
        activated: 'true',
        registration_activated: 'true',
      };

      axios.patch(`/api/v1/influencers/users/${userId}`, {
        user
      }).then(() => {
        dispatch(updateUser(values));
        if (!isMounted) return;
        setState({ ...state, loading: false });
        history.push('/');
      }).catch(() => {
        if (!isMounted) return;
        setState({ ...state, loading: false });
        formik.setErrors({ city: 'Unexpected error' });
      });
    },
  });

  const onLoadCities = useCallback(
    /**
     * @param {string} searchCity
     */
    (searchCity) => {
      if (!searchCity || searchCity === "" || searchCity.length < 3) return;

      setState({
        ...state,
        loadingCities: true,
      });
      return new Promise((resolve) => {
        axios.get(`/api/v1/cities?query=${searchCity}`)
          .then(({ data }) => {
            const cities = data.cities.map(city => ({
              ...city,
              value: city.id
            }));
            resolve(cities);
          });
      });
    },
    []);

  const debouncedLoadCities = useRef(debounce(onLoadCities, 300)).current;

  useEffect(() => () => {
    debouncedLoadCities.cancel();
  }, [debouncedLoadCities]);

  /**
   * Wraps formik.handleChange and clears stale validation errors.
   * Formik's async validation can run with old values on mobile, leaving errors
   * for fields that are now valid. After a valid value is set, we re-validate
   * and clear any leftover error so the Next button can enable.
   */
  const handleChangeWithStaleErrorFix = (fieldName) => (e) => {
    const val = get(e, 'target.value');

    formik.handleChange(e);

    if (val != null && val !== '') {
      formik.validateForm().then((errors) => {
        if (errors && errors[fieldName]) {
          formik.setFieldError(fieldName, undefined);
        }
      });
    }
  };

  return (<Row>
    <Col xs={12} lg={5}>
      <div className="body-3 mb-2">
        {i18n.t('influencers.common.step_of', {
          now: 3,
          end: 3
        })}
      </div>
      <ResponsiveHeader xs="h3" lg="h2">
        {i18n.t('influencers.pages.sign_up.matching_with_your_brands')}
      </ResponsiveHeader>
      <div className="body-1 mb-4">
        {i18n.t('influencers.pages.sign_up.matching_with_your_brands_desc')}
      </div>
    </Col>
    <Col xs={12} lg={{
      span: 6,
      offset: 1
    }}>
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>
                {i18n.t('influencers.common.city')}
              </Form.Label>

              <AsyncSelect
                type='text'
                name="city_id"
                data-cy="city"
                cacheOptions
                onChange={handleChangeWithStaleErrorFix('city_id')}
                onBlur={formik.handleBlur}
                onMenuClose={() => formik.setFieldTouched('city_id', true)}
                value={formik.values.city_id}
                error={Boolean(get(formik, 'touched.city_id') && get(formik, 'errors.city_id'))}
                loadOptions={debouncedLoadCities}
                isClearable
              />
              <Form.Text className="text-muted danger-text">
                {get(formik, 'touched.city_id') && get(formik, 'errors.city_id')}
              </Form.Text>

            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>
                {i18n.t('influencers.common.birthday')}
              </Form.Label>
              <FormControl
                type='date'
                max={moment(new Date()).format('YYYY-MM-DD')}
                name="dob"
                data-cy="dob"
                onChange={handleChangeWithStaleErrorFix('dob')}
                onBlur={formik.handleBlur}
                value={formik.values.dob}
                error={Boolean(formik.touched.dob && formik.errors.dob)}
              />
              <Form.Text className="text-muted danger-text">
                {formik.touched.dob && formik.errors.dob}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label>
                {i18n.t('influencers.common.gender')}
              </Form.Label>
              <Select
                type='text'
                name="gender"
                data-cy="gender"
                onChange={handleChangeWithStaleErrorFix('gender')}
                onBlur={formik.handleBlur}
                onMenuClose={() => formik.setFieldTouched('gender', true)}
                value={formik.values.gender}
                error={Boolean(formik.touched.gender && formik.errors.gender)}
                options={
                  GENDER_TYPES.map(gender => ({
                    label: i18n.t(`influencers.pages.profile.campaign.${snakeCase(gender)}`,
                      { defaultValue: gender }),
                    value: gender
                  }))
                }
              />
              <Form.Text className="text-muted danger-text">
                {formik.touched.gender && formik.errors.gender}
              </Form.Text>
            </Form.Group>
          </Col>

        </Row>

        <hr className={classNames('my-6', {
          noMargin: breakpoint === 'xs'
        })} />

        <div className='flex flex-column align-items-end'>
          <ButtonGroup className="mb-4">
            <Button
              variant="primary"
              type="submit"
              disabled={!formik.dirty || state.loading || !formik.isValid}>
              {state.loading
                ? <Spinner animation="border" size="sm" />
                : i18n.t('influencers.common.next')
              }
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </Col>
  </Row>);
};

SignUpStep3.propTypes = {
  user: PropTypes.object,
};

export default SignUpStep3;
